import type {
  BuildCategoricalChartOptionsInput,
  CategoricalChartOptions,
} from './chartBuilder.types'
import { category, finiteNumber, groupRows } from './chartData'
import { buildChartTooltip, chartFieldLabel, formatChartValue } from './chartPresentation'
import { firstField, type ResolvedChartFields } from './resolveChartFields'

const BUBBLE_POINT_LIMIT = 20
const AXIS_LABEL_COLOR = 'var(--ui-chart-label)'
const AXIS_TITLE_COLOR = 'var(--ui-chart-axis-title)'

function bubbleRows(
  input: BuildCategoricalChartOptionsInput,
  metricX: string,
  metricY: string,
  metricSize: string,
) {
  return input.rows
    .filter(
      (row) =>
        finiteNumber(row[metricX]) > 0 &&
        finiteNumber(row[metricY]) > 0 &&
        finiteNumber(row[metricSize]) > 0,
    )
    .sort((left, right) => finiteNumber(right[metricX]) - finiteNumber(left[metricX]))
    .slice(0, BUBBLE_POINT_LIMIT)
}

export function buildCorrelationChartOptions(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
  bubble: boolean,
): CategoricalChartOptions | null {
  const metricX = firstField(fields, ['metric_x'])
  const metricY = firstField(fields, ['metric_y'])
  const metricSize = bubble ? firstField(fields, ['metric_size']) : undefined
  const dimension = firstField(fields, ['dimension'])
  if (!metricX || !metricY || (bubble && !metricSize)) return null

  const rows = bubble && metricSize ? bubbleRows(input, metricX, metricY, metricSize) : input.rows
  if (rows.length === 0) return null
  const groups = groupRows(rows, dimension)
  const metricName = `${chartFieldLabel(input, metricX)} / ${chartFieldLabel(input, metricY)}`
  const series = [...groups.entries()].map(([group, groupRows]) => ({
    type: bubble ? 'bubble' : 'scatter',
    name: group || metricName,
    ...(bubble ? { marker: { fillOpacity: 0.9 } } : {}),
    data: groupRows.map((row) => ({
      x: finiteNumber(row[metricX]),
      y: finiteNumber(row[metricY]),
      ...(metricSize ? { z: finiteNumber(row[metricSize]) } : {}),
      ...(dimension ? { name: category(row[dimension]) } : {}),
    })),
  }))
  if (!bubble || !metricSize) {
    return {
      chart: { type: 'scatter' },
      title: { text: '' },
      xAxis: { title: { text: chartFieldLabel(input, metricX) } },
      yAxis: { title: { text: chartFieldLabel(input, metricY) } },
      series,
      credits: { enabled: false },
    } as CategoricalChartOptions
  }

  const xValues = rows.map((row) => finiteNumber(row[metricX]))
  const minimumX = Math.min(...xValues)
  const maximumX = Math.max(...xValues)
  return {
    chart: {
      type: 'bubble',
      plotBorderWidth: 1,
      plotBorderColor: 'var(--ui-chart-axis)',
      zooming: { type: 'xy' },
    },
    legend: { enabled: true, useHTML: true, itemStyle: { fontSize: '12px' } },
    title: {
      text: dimension ? chartFieldLabel(input, dimension) : '',
      style: { fontSize: '12px', fontWeight: '700' },
    },
    xAxis: {
      max: maximumX * 1.2,
      min: minimumX * 0.8 || 0.1,
      type: 'logarithmic',
      gridLineWidth: 0,
      title: {
        text: chartFieldLabel(input, metricX),
        style: {
          color: AXIS_TITLE_COLOR,
          fontSize: '12px',
          fontWeight: '700',
        },
      },
      labels: {
        style: { color: AXIS_LABEL_COLOR, fontSize: '11px', fontWeight: '400' },
        formatter(this: { value: number }) {
          return formatChartValue(input, this.value, metricX)
        },
      },
    },
    yAxis: {
      gridLineWidth: 0,
      startOnTick: false,
      endOnTick: false,
      type: 'logarithmic',
      maxPadding: 0.2,
      title: {
        text: chartFieldLabel(input, metricY),
        style: {
          color: AXIS_TITLE_COLOR,
          fontSize: '12px',
          fontWeight: '700',
        },
      },
      labels: {
        style: { color: AXIS_LABEL_COLOR, fontSize: '11px', fontWeight: '400' },
        formatter(this: { value: number }) {
          return formatChartValue(input, this.value, metricY)
        },
      },
    },
    tooltip: {
      useHTML: true,
      outside: true,
      headerFormat: '',
      formatter() {
        const context = this as unknown as {
          point: { color?: unknown; name: unknown; x: unknown; y: unknown; z: unknown }
        }
        return buildChartTooltip(context.point.name, [
          {
            color: context.point.color,
            label: chartFieldLabel(input, metricX),
            value: formatChartValue(input, context.point.x, metricX),
          },
          {
            color: context.point.color,
            label: chartFieldLabel(input, metricY),
            value: formatChartValue(input, context.point.y, metricY),
          },
          {
            color: context.point.color,
            label: chartFieldLabel(input, metricSize),
            value: formatChartValue(input, context.point.z, metricSize),
          },
        ])
      },
    },
    plotOptions: {
      bubble: {
        minSize: 30,
        maxSize: 50,
        dataLabels: {
          enabled: true,
          format: '{point.name}',
          style: {
            fontSize: '11px',
            color: AXIS_LABEL_COLOR,
            textOutline: 'none',
            fontWeight: '500',
          },
          inside: false,
          y: -10,
          overflow: 'allow',
          crop: false,
        },
      },
    },
    series,
    credits: { enabled: false },
  } as CategoricalChartOptions
}
